/**
 * ConfirmCard — карточка подтверждения опасной команды.
 *
 * Раньше backend возвращал в чат текст вида
 *   «Требуется подтверждение: `подтверждаю ed97df51`»
 * и пользователь должен был вручную копировать `подтверждаю ed97df51`
 * в инпут и отправлять. Теперь вместо этого парсим confirm_id из текста
 * сообщения и рисуем кнопку «Подтвердить» (вызывает POST /api/v1/confirm/{id}).
 *
 * Endpoints:
 *   POST /api/v1/confirm/{id}          — выполнить
 *   POST /api/v1/confirm/{id}/reject   — отменить
 */

import { useState } from "react";
import { Check, X, Loader2, ShieldAlert } from "lucide-react";
import { motion } from "framer-motion";
import { confirmCommand, rejectCommand } from "../../lib/api";
import { cn } from "../../lib/cn";

export interface ConfirmCardProps {
  confirmId: string;
  /** Превью команды (опционально, если backend прислал его в тексте). */
  preview?: string;
}

type State =
  | { kind: "idle" }
  | { kind: "loading"; action: "confirm" | "reject" }
  | { kind: "confirmed"; result?: string; command?: string }
  | { kind: "rejected" }
  | { kind: "error"; message: string };

export function ConfirmCard({ confirmId, preview }: ConfirmCardProps) {
  const [state, setState] = useState<State>({ kind: "idle" });

  async function handleConfirm() {
    setState({ kind: "loading", action: "confirm" });
    try {
      const res = await confirmCommand(confirmId);
      if (!res.success) {
        setState({ kind: "error", message: res.error || "Не удалось подтвердить" });
        return;
      }
      setState({ kind: "confirmed", result: res.result, command: res.command });
    } catch (e) {
      setState({
        kind: "error",
        message: e instanceof Error ? e.message : String(e),
      });
    }
  }

  async function handleReject() {
    setState({ kind: "loading", action: "reject" });
    try {
      const res = await rejectCommand(confirmId);
      if (!res.success) {
        setState({ kind: "error", message: res.error || "Не удалось отменить" });
        return;
      }
      setState({ kind: "rejected" });
    } catch (e) {
      setState({
        kind: "error",
        message: e instanceof Error ? e.message : String(e),
      });
    }
  }

  const idShort = confirmId.slice(0, 8);

  // ─── Финальные состояния (после клика) ─────────────────────────────────
  if (state.kind === "confirmed") {
    // Подтверждение прошло, но backend выполнил команду — она могла вернуть
    // ненулевой код выхода («Ошибка (код 1): ...»). Это не баг подтверждения,
    // но пользователю важно увидеть, что команда упала.
    const failed = isResultError(state.result);
    return (
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn(
          "mt-3 rounded-xl border p-3",
          failed
            ? "border-warning/40 bg-warning/5"
            : "border-success/40 bg-success/5",
        )}
      >
        <div
          className={cn(
            "flex items-center gap-2 font-medium text-small",
            failed ? "text-warning" : "text-success",
          )}
        >
          {failed ? <ShieldAlert className="size-4" /> : <Check className="size-4" />}
          {failed ? "Команда выполнена с ошибкой" : "Команда подтверждена и выполнена"}
        </div>
        {state.command && (
          <pre className="mt-2 px-2 py-1.5 rounded-md bg-bg-subtle text-[0.78em] font-mono text-fg-muted overflow-x-auto">
            {state.command}
          </pre>
        )}
        {state.result && state.result.trim() && (
          <pre className="mt-2 px-2 py-1.5 rounded-md bg-bg-subtle text-[0.78em] font-mono text-fg-muted whitespace-pre-wrap max-h-40 overflow-y-auto">
            {state.result.length > 1500 ? state.result.slice(0, 1500) + "\n…" : state.result}
          </pre>
        )}
      </motion.div>
    );
  }

  if (state.kind === "rejected") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-3 rounded-xl border border-border/60 bg-bg-subtle/40 p-3 flex items-center gap-2 text-fg-muted text-small"
      >
        <X className="size-4" />
        Команда отменена.
      </motion.div>
    );
  }

  // ─── idle / loading / error — показываем кнопки ──────────────────────
  const loading = state.kind === "loading";
  const errorMsg = state.kind === "error" ? state.message : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-3 rounded-xl border border-warning/40 bg-warning/5 p-3"
    >
      <div className="flex items-start gap-2">
        <ShieldAlert className="size-4 mt-0.5 shrink-0 text-warning" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-small text-fg">
            Требуется подтверждение
          </div>
          <div className="text-[0.8em] text-fg-muted mt-0.5">
            Команда помечена как опасная. Подтвердите, чтобы выполнить.
          </div>
          {preview && preview.trim() && (
            <pre className="mt-2 px-2 py-1.5 rounded-md bg-bg-subtle text-[0.78em] font-mono text-fg-muted overflow-x-auto">
              {preview.length > 240 ? preview.slice(0, 240) + "…" : preview}
            </pre>
          )}
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-small font-medium",
                "text-white shadow-accent transition-opacity",
                loading ? "opacity-60 cursor-wait" : "hover:opacity-90",
              )}
              style={{ backgroundImage: "var(--grad-cta)" }}
              aria-label="Подтвердить выполнение команды"
            >
              {loading && state.kind === "loading" && state.action === "confirm" ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Check className="size-3.5" />
              )}
              Подтвердить
            </button>
            <button
              type="button"
              onClick={handleReject}
              disabled={loading}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-small font-medium",
                "border border-border/60 text-fg-muted hover:bg-bg-subtle transition-colors",
                loading && "opacity-60 cursor-wait",
              )}
              aria-label="Отменить команду"
            >
              {loading && state.kind === "loading" && state.action === "reject" ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <X className="size-3.5" />
              )}
              Отклонить
            </button>
            <span className="text-[0.72em] font-mono text-fg-subtle ml-1">
              id: {idShort}
            </span>
          </div>
          {errorMsg && (
            <div className="mt-2 text-small text-danger">
              {errorMsg}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/**
 * isResultError — определяет, является ли результат выполнения команды ошибкой.
 * Backend (jarvis-core/app/core/agent/tools.py) форматирует ошибки как
 * «Ошибка (код N): ...». Также ловим явные ERROR-префиксы.
 */
function isResultError(result?: string): boolean {
  if (!result) return false;
  const s = result.trim();
  if (/^Ошибка\s*\(код\s+\d+\)/i.test(s)) return true;
  if (/^ERROR\b/i.test(s)) return true;
  return false;
}

/**
 * extractConfirmId — извлекает confirm_id из текста сообщения ассистента.
 *
 * Backend в done.text вставляет фразу
 *   `Требуется подтверждение: \`подтверждаю ed97df51\``
 * либо
 *   `⚠️ ТРЕБУЕТСЯ ПОДТВЕРЖДЕНИЕ [ed97df51]: '...'`
 *
 * Возвращает первый найденный 8-hex id или null.
 */
export function extractConfirmId(text: string): string | null {
  if (!text) return null;
  // Сначала ищем «подтверждаю xxxxxxxx» (приоритетно — это явный контракт).
  const m1 = text.match(/подтверждаю\s+([0-9a-fA-F]{6,12})/);
  if (m1) return m1[1];
  // Запасной паттерн — `[xxxxxxxx]` после слова «ПОДТВЕРЖДЕНИЕ».
  const m2 = text.match(/ПОДТВЕРЖДЕНИЕ\s*\[([0-9a-fA-F]{6,12})\]/);
  if (m2) return m2[1];
  return null;
}

/**
 * extractConfirmPreview — пытается достать сниппет команды из текста.
 * Используется опционально, чтобы показать пользователю «что именно подтверждаешь».
 */
export function extractConfirmPreview(text: string): string | undefined {
  if (!text) return undefined;
  // ⚠️ ТРЕБУЕТСЯ ПОДТВЕРЖДЕНИЕ [id]: 'команда'
  const m = text.match(/ПОДТВЕРЖДЕНИЕ\s*\[[0-9a-fA-F]{6,12}\]\s*:\s*['"`]?([^\n'"`]+)['"`]?/);
  if (m && m[1]) return m[1].trim();
  return undefined;
}
